/* eslint-disable react/jsx-one-expression-per-line */
import React, {
    useContext, useRef, useEffect, useState, useMemo, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import queryString from 'query-string';
import { gql, useQuery, useReactiveVar, useSuspenseQuery } from '@apollo/client';
import ScrollLock from 'react-scrolllock';
import LINKS from 'app/const/links';
import { OverlayContextDispatch } from 'app/containers//contexts/overlay.context';
import { LocaleContext, LOCALE_RU, LOCALE_EN } from 'app/containers/LocaleProvider';
import { BasketFrame } from 'app/views/Basket/CommonComponents/BasketFrame';

import { UIButton, UIHeading } from 'app/components/ui';
import OpenBasketButton from 'app/components/OpenBasketButton';
import MobileCartHeader from 'app/components/MobileCartHeader';
import Dishes from 'app/views/Basket/components/BasketDishes';
import TrialBasketDescription from 'app/views/Basket/components/TrialBasket/TrialBasketDescription';
import MobileDishDetails from 'app/views/MobileDishDetails/MobileDishDetails';
import PREVIEW_CART_FOR_SET from '../../graphql/network/previewCartForSet';

import '../../views/Basket/basket.scss';
import '../../views/Basket/components/BasketCategories/basket-categories.scss';
import '../../views/Basket/components/TrialBasket/styles/trialBasketDescription.scss';
import '../CustomizationComment/customization-comment.scss';
import './basket-preview.scss';
import { useBasketData } from 'app/hooks/accessors';
import { selectedPeriodVar } from 'app/apollo/reaction';
import { OpenBasketButtonForPreview } from '../OpenBasketButton/OpenBasketButtonForPreview';


const {
    TRIAL_FIRST_ORDER_SET,
    EVERYDAY_SET,
    PREMIUM_SET,
    OUR_SET,
    PREMIUM_SET_GREEN_VERSION,
} = LINKS;


const noop = () => { };


const textOptions = [
    {
        sets: [TRIAL_FIRST_ORDER_SET?.id],
        title: {
            ru: {
                row_1: 'Пробный сет',
                row_2: 'из 5 ужинов',
            },
            en: {
                row_1: '5 Dishes',
                row_2: 'First Order',
            },
        },
        description: {
            ru: {
                row_1: 'Для знакомства с Elementaree',
                row_2: '- подборка супер-блюд от шефа!',
                row_3: (
                    <>
                        Новым клиентам скидка
                        {/* &nbsp;
                        <span styleName="basket-preview__crossed">{TRIAL_FIRST_ORDER_SET.originalPrice} ₽</span> */}
                    </>
                ),
            },
            en: {
                row_1: 'Get your first 5 dishes pack tomorrow!',
                row_2: (
                    <>
                        Discount for new clients
                        {/* &nbsp;
                        <span styleName="basket-preview__crossed">{TRIAL_FIRST_ORDER_SET.originalPrice} ₽</span> */}
                    </>
                ),
            },
        },
        price: TRIAL_FIRST_ORDER_SET.originalPrice,
        isPriceDynamic: false,
    },
    {
        sets: [OUR_SET?.id],
        title: {
            ru: {
                row_1: 'Топ-5',
                row_2: 'ужинов недели',
            },
            en: {
                row_1: 'ROW 2 EN',
                row_2: 'ROW 2 EN',
            },
        },
        description: {
            ru: {
                row_1: 'Сет из самых интересных блюд недели. Новые вкусы ждут!',
                // row_2: '',
                row_3: (
                    <>
                        Скидка 10% по подписке

                    </>
                ),
            },
            en: {
                row_1: 'Get your first 5 dishes pack tomorrow!',
                row_2: (
                    <>
                        Discount for new clients
                    </>
                ),
            },
        },
        price: '3600',
        isPriceDynamic: true,
    },
    {
        sets: [EVERYDAY_SET.id],
        title: {
            ru: {
                row_1: 'Сет недели',
                row_2: 'из 5 ужинов',
            },
            en: {
                row_1: '5 Dishes',
                row_2: 'Family Pack',
            },
        },
        description: {
            ru: {
                row_1: 'Меню недели по выгодной цене!',
                row_2: 'И мы готовы привезти его уже завтра.',
                row_3: (
                    <>
                        Скидка {EVERYDAY_SET.discountPercent}% по подписке
                        &nbsp;
                        <span styleName="basket-preview__crossed">{EVERYDAY_SET.originalPrice} ₽</span>
                    </>
                ),
            },
            en: {
                row_1: 'Weekly menu from all over the world,',
                row_2: 'delivered tomorrow!',
                row_3: (
                    <>
                        {EVERYDAY_SET.discountPercent}% off for subscribers
                        &nbsp;
                        <span styleName="basket-preview__crossed">{EVERYDAY_SET.originalPrice} ₽</span>
                    </>
                ),
            },
        },
        price: EVERYDAY_SET.discountPrice,
        isPriceDynamic: false,
    },

    {
        sets: [PREMIUM_SET_GREEN_VERSION.id],
        title: {
            ru: {
                row_1: 'Премиум сет',
                row_2: 'из 5 ужинов',
            },
            en: {
                row_1: '5 Dishes',
                row_2: 'Premium Pack',
            },
        },
        description: {
            ru: {
                row_1: 'Устройте ресторан прямо у себя',
                row_2: 'дома с подборкой авторских блюд! ',
                row_3: (
                    <>
                        Скидка {PREMIUM_SET_GREEN_VERSION.discountPercent}% по подписке
                        &nbsp;
                        <span styleName="basket-preview__crossed">{PREMIUM_SET_GREEN_VERSION.originalPrice} ₽</span>
                    </>
                ),
            },
            en: {
                row_1: 'Restaurant-like dishes with homemade',
                row_2: 'sauces by our chief.',
                row_3: (
                    <>
                        {PREMIUM_SET.discountPercent}% off for subscribers
                        &nbsp;
                        <span styleName="basket-preview__crossed">{PREMIUM_SET_GREEN_VERSION.originalPrice} ₽</span>
                    </>
                ),
            },
        },
        price: PREMIUM_SET.discountPrice,
        isPriceDynamic: false,
    },
];

const getTexts = (id, locale) => {
    const options = textOptions.find((op) => op.sets.includes(id));

    const { price, isPriceDynamic } = options;

    return {
        price,
        isPriceDynamic,
        title: options.title[locale],
        description: options.description[locale],
    };
};


const mockingDishesProps = {
    scrollbarsRef: { current: null },

    isSubscriptionActivated: false,
    isCategoryOpened: true,
    isOneItemInCategory: true,

    onChangePortion: noop,
    onRemoveClick: noop,
    onClickMobilePortions: noop,
    openDishDetails: noop,
};

const SetTitle = ({
    onChooseSet,
    locale,
    texts,
    price,
}) => (
    <div styleName="basket-trial-description__title basket-preview__top-area__first-row">
        <UIHeading level="3.2" styleName="basket-preview__title">
            {texts.row_1}
            <br />
            {texts.row_2}
        </UIHeading>

        {/* <UIButton
            onClick={onChooseSet}
            aria-label="Заполнить корзину"
            styleName="basket-preview__set-button"
            sizeType="medium"
        >
            <span className="price">
                {locale === 'en' && (<span className="rub">₽</span>)}
                <span className="num">{price}</span>
                {locale === 'ru' && (<span className="rub">₽</span>)}
            </span>
        </UIButton> */}
    </div>
);

SetTitle.propTypes = {
    onChooseSet: PropTypes.func.isRequired,
    locale: PropTypes.oneOf([LOCALE_RU, LOCALE_EN]).isRequired,
    texts: PropTypes.shape({
        row_1: PropTypes.string,
        row_2: PropTypes.string,
    }).isRequired,
    price: PropTypes.number,
};


const CART_DATA_FOR_PREVIEW_QUERY = gql`
    query CART_DATA_FOR_PREVIEWT(
        $period: String!
    ) {
        cart(
            period: $period
        ) {
            type
            period
            sections {
                cartType
                id
            }
        }
    }
`;

//  previewCartForSet

const PREVIEW_CART_PRICE_QUERY = gql`
    query PREVIEW_CART_PRICE_QUERY(
        $period: String!,
        $set_type: cartSetType,
    ) {
        previewCartForSet(period: $period, set_type: $set_type) {
            type
            id
            totals {
                discount_amount
                static_common_price
                total_common_price
                subscriptionPrice
                discount_applied
            }
            sections {
                cartType
                id
            }
            totals {
                total_common_price
            }
        }
    }
`;


const BasketPreview = (props) => {
    const {
        setId,
        period,
        setType,
        onChooseSet,
        onClickBack,
        isRecurrent = false,
    } = props;

    const { locale } = useContext(LocaleContext);
    const { pushOverlay, closeLastOverlays } = useContext(OverlayContextDispatch);

    const scrollableContentRef = useRef({});
    const [isMounted, setIsMounted] = useState(false);

    // const { data } = useQuery(PREVIEW_CART_PRICE_QUERY, {
    //     variables: {
    //         period,
    //         set_type: setType,
    //     },
    //     fetchPolicy: 'cache-first',
    // });


    // const { previewCartForSet: cart } = cartData;


    // SL
    // const isBasketEmpty = useMemo(
    //     () => cart?.sections?.length > 0 || true,
    //     [cart?.sections?.length],
    // );

    const {
        title, description, price: priceValue, isPriceDynamic,
    } = useMemo(
        () => getTexts(setId, locale, period),
        [setId, locale, period],
    );

    const queryVars = {
        period,
        set_type: setType,
    };

    const {
        loading, error, data,
    } = useSuspenseQuery(PREVIEW_CART_FOR_SET, {
        variables: queryVars,
        context: {
            message: 'menu:init:BasketPreview',
        },
        fetchPolicy: 'network-only',
    });

    const previewSections = data?.previewCartForSet?.sections;
    const price = isRecurrent
        ? data?.previewCartForSet?.totals?.subscriptionPrice || data?.previewCartForSet?.totals?.total_common_price
        : data?.previewCartForSet?.totals?.total_common_price;
    const previewTotals = data?.previewCartForSet.totals;
    const {
        total_common_price: totalCommonPriceFromPreview,
        static_common_price: staticCommonPriceFromPreview,
    } = previewTotals || {};

    const handleClickMobileDishDetails = useCallback((mobileDishDetailsInfo) => pushOverlay({
        id: 'dish_details',
        template: 'bottom',
        children: (
            <MobileDishDetails
                info={mobileDishDetailsInfo}
                onClose={closeLastOverlays}
            />
        ),
    }), [closeLastOverlays, pushOverlay]);


    useEffect(() => {
        setIsMounted(true);
        return () => {
            setIsMounted(false);
        };
    }, [setIsMounted]);

    const rootClasses = classNames({
        basket: true,
        // 'on-discount-counter-unmount': !isBasketEmpty,
    });

    const scrollContainerClasses = classNames({
        'basket-categories__scroll-content': true,
        'basket-categories__scroll-content--preview': true,
        basket__dishes: true,
    });

    const categoryClasses = classNames({
        'basket__dishes-type': true,
        preview: true,
        open: true,
        small: true,
    });

    const preparedBasketAreas = {
        basketStyleModifier: 'PREVIEW_SET',
        list: {
            titleType: 'regular',
        },
    };

    return (
        <BasketFrame>
            <div styleName="basket-preview__content-wrapper">
                <div styleName="basket-header">
                    <MobileCartHeader
                        isLeftBlockShown
                        isRightBlockShown={false}
                        onClickBack={onClickBack}
                        position="fixed"
                        realm="preview"
                    />
                </div>

                <TrialBasketDescription
                    texts={description}
                    price={isPriceDynamic ? totalCommonPriceFromPreview : priceValue}
                    staticCommonPrice={isPriceDynamic ? staticCommonPriceFromPreview : undefined}
                    TitleComponent={(
                        <SetTitle
                            onChooseSet={onChooseSet}
                            locale={locale}
                            texts={title}
                            price={isPriceDynamic ? totalCommonPriceFromPreview : priceValue}
                        />
                    )}
                />

                {/* {isMounted && (
                    <ScrollLock touchScrollTarget={scrollableContentRef.current} />
                )} */}

                <div styleName={scrollContainerClasses} ref={scrollableContentRef}>
                    <div styleName={categoryClasses}>
                        {!(loading || error) && (
                            <Dishes
                                items={previewSections[0].items}
                                locale={locale}
                                isEditEnabled={false}
                                preparedBasketAreas={preparedBasketAreas}
                                onClickMobileDishDetails={handleClickMobileDishDetails}
                                sectionId={previewSections[0].id}
                                {...mockingDishesProps}
                            />
                        )}
                    </div>
                </div>
                <div styleName="basket-footer">
                    <OpenBasketButtonForPreview
                        price={price}
                        position="relative"
                        onClick={onChooseSet}
                        loading={false}
                        title=""
                        showPlusIcon
                    />
                </div>
            </div>
        </BasketFrame>
    );
};

BasketPreview.propTypes = {
    setId: PropTypes.string.isRequired,
    period: PropTypes.string.isRequired,
    setType: PropTypes.string.isRequired,

    onChooseSet: PropTypes.func.isRequired,
    onClickBack: PropTypes.func.isRequired,
};

export default BasketPreview;
